/**
 * Watched-coverage bookkeeping for SCORM completion integrity.
 *
 * Completion must reflect what the learner actually watched, not where the
 * playhead happens to be — seeking to the end of the video must never count.
 * The player records [start, end] segments of *natural* playback (consecutive
 * timeupdate ticks) and completion is computed from the merged coverage of
 * those segments. The segments are also serialized into cmi.suspend_data so
 * coverage (and the exact resume position) survives exit/relaunch.
 */

export type WatchedSegment = [start: number, end: number];

export const SUSPEND_DATA_VERSION = 1;

/**
 * Gaps smaller than this are bridged when merging segments. Natural playback
 * produces hairline gaps at segment boundaries (timeupdate granularity,
 * pause/resume, tab switches); bridging them keeps a fully-watched video at
 * ~100% coverage without ever crediting a real skip.
 */
export const SEGMENT_MERGE_GAP_SECONDS = 0.75;

/** SCORM 1.2 caps cmi.suspend_data at 4096 chars; stay safely below. */
const MAX_SUSPEND_DATA_LENGTH = 3800;

const round = (value: number) => Math.round(value * 10) / 10;

export function mergeSegments(
  segments: readonly WatchedSegment[],
  gapTolerance: number = SEGMENT_MERGE_GAP_SECONDS,
): WatchedSegment[] {
  const sorted = segments
    .filter(
      ([start, end]) =>
        Number.isFinite(start) && Number.isFinite(end) && end > start && start >= 0,
    )
    .map(([start, end]): WatchedSegment => [start, end])
    .sort((a, b) => a[0] - b[0]);

  const merged: WatchedSegment[] = [];
  for (const [start, end] of sorted) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1] + gapTolerance) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

/** Total seconds covered by the (merged) segments. */
export function coverageSeconds(segments: readonly WatchedSegment[]): number {
  return mergeSegments(segments).reduce((sum, [start, end]) => sum + (end - start), 0);
}

export type ScormSuspendData = {
  v: number;
  /** Last playhead position (exact resume point), seconds. */
  t: number;
  /** Furthest naturally-watched position, seconds — seeds the forward-seek lock. */
  max: number;
  /** Watched segments [start, end] in seconds. */
  seg: WatchedSegment[];
};

export function serializeSuspendData(data: {
  t: number;
  max: number;
  seg: readonly WatchedSegment[];
}): string {
  let gap = SEGMENT_MERGE_GAP_SECONDS;
  let seg = mergeSegments(data.seg, gap).map(
    ([start, end]): WatchedSegment => [round(start), round(end)],
  );
  const encode = () =>
    JSON.stringify({
      v: SUSPEND_DATA_VERSION,
      t: round(data.t),
      max: round(data.max),
      seg,
    });

  let json = encode();
  // Extremely fragmented viewing could overflow the SCORM 1.2 suspend_data
  // limit; coarsen the merge until it fits. Coarser merging slightly
  // over-credits coverage but never loses the resume point.
  while (json.length > MAX_SUSPEND_DATA_LENGTH && seg.length > 1) {
    gap *= 4;
    seg = mergeSegments(seg, gap);
    json = encode();
  }
  return json;
}

export function parseSuspendData(raw: string | null | undefined): ScormSuspendData | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const candidate = parsed as Partial<ScormSuspendData>;
    const t = Number(candidate.t);
    const max = Number(candidate.max);
    const seg = Array.isArray(candidate.seg)
      ? mergeSegments(
          candidate.seg
            .filter(
              (entry): entry is WatchedSegment =>
                Array.isArray(entry) && entry.length === 2,
            )
            .map(([start, end]): WatchedSegment => [Number(start), Number(end)]),
        )
      : [];
    return {
      v: Number(candidate.v) || SUSPEND_DATA_VERSION,
      t: Number.isFinite(t) && t > 0 ? t : 0,
      max: Number.isFinite(max) && max > 0 ? max : 0,
      seg,
    };
  } catch {
    return null;
  }
}
